import {focusElement, hookToAttributeSelector} from '@wix/panda-js-utils'
import {ExperimentNames} from '@wix/wix-events-commons-statics'
import {detailsPageDataHooks as DH} from '@wix/wix-events-data-hooks'
import {useExperiments} from '@wix/yoshi-flow-editor'
import React, {useEffect} from 'react'
import {Text, TextTypography} from 'wix-ui-tpa/cssVars'
import {hasHeadlessTYP} from '../../../../../../commons/selectors/environment'
import {usePageStateSelector} from '../../../hooks/state'
import {areAllTicketsSentToBuyerEmail} from '../../../selectors/order'
import {GroupCard} from '../../group-card'
import {Spinner} from '../../spinner'
import {Container} from '../common/container'
import {Subtitle} from '../common/subtitle'
import {SuccessActions} from '../common/success-actions'
import {Title} from '../common/title'
import {LinkExpired} from '../link-expired'
import {Desktop} from './desktop'
import {Mobile} from './mobile'
import s from './order-success.scss'
import {classes} from './order-success.st.css'
import {OrderSuccessProps} from '.'

export const OrderSuccess = (props: OrderSuccessProps) => {
  const {t, eventTitle, dateTbd, template, orderPending, isAssignedTickets, order, showGroup, loading} = props
  let ticketsSentMsg = 'ticketsThankYou.ticketSent'
  const noExpiredLink = usePageStateSelector(hasHeadlessTYP)
  const {experiments} = useExperiments()
  const updateOrderSuccessContentEnabled = experiments.enabled(ExperimentNames.UpdateOrderSuccessContent)

  useEffect(() => {
    if (!template && order) {
      props.ticketedThankYouPageLoaded()
    }

    focusElement({selector: hookToAttributeSelector(DH.ORDER_SUCCESS_TITLE)})
  }, [])

  if (loading) {
    return (
      <div className={s.spinnerContainer}>
        <Spinner diameter={100} className={classes.spinner} type="slim" />
        <Text typography={TextTypography.smallTitle}>{t('orderSuccess_loadingText')}</Text>
      </div>
    )
  }

  if (orderPending) {
    if (order.ticketsQuantity === 1) {
      ticketsSentMsg = 'ticketsThankYou.regProcessing.singleTicket'
    } else {
      ticketsSentMsg = 'ticketsThankYou.regProcessing.multipleTickets'
    }
  } else if (isAssignedTickets) {
    if (areAllTicketsSentToBuyerEmail(order) && updateOrderSuccessContentEnabled) {
      ticketsSentMsg = 'ticketsThankYou.ticketsSentWithAssignedTickets.onlySentToBuyer'
    } else {
      ticketsSentMsg = 'ticketsThankYou.ticketsSentWithAssignedTickets'
    }
  }

  const downloadTicketsVisible = !template && !orderPending

  const addToCalendarVisible = !dateTbd

  if (!order) {
    return noExpiredLink ? null : <LinkExpired />
  }

  const {firstName, email, tickets} = order
  const ticketsCount = tickets?.length ?? 0

  return (
    <Container dataHook={DH.ORDER_SUCCESS}>
      <div className={s.header}>
        <Title className={s.title} text={t('ticketsThankYou.thanks', {firstName})} dataHook={DH.ORDER_SUCCESS_TITLE} />
        <Subtitle
          lines={[t('ticketsThankYou.regComplete', {eventTitle}), t(ticketsSentMsg, {email, count: ticketsCount})]}
          dataHook={DH.ORDER_SUCCESS_SUBTITLE}
          noBottomMargin={!addToCalendarVisible && !downloadTicketsVisible}
        />
        <SuccessActions
          t={t}
          className={s.successActions}
          addToCalendarVisible={addToCalendarVisible}
          downloadTicketsVisible={downloadTicketsVisible}
        />
        {showGroup ? <GroupCard /> : null}
      </div>
      <div className={s.desktop} data-hook={DH.DESKTOP}>
        <Desktop {...props} />
      </div>
      <div className={s.mobile} data-hook={DH.MOBILE}>
        <Mobile {...props} />
      </div>
    </Container>
  )
}
